import { type Map as ImMap, List, type RecordOf } from 'immutable';

import { accountsSelectors } from '../accounts';
import type { Account } from '../accounts/accountsTypes';

import { getInstitutionsById } from 'app-components/flux/institutions/institutionsSelectors';
import {
  getAggregatorFromChannel,
  getStaticInstitutionFromAggregator,
} from 'app-components/flux/institutions/institutionsUtils';
import { createAppSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';

import { type AggStatus, INSTITUTION_LOGINS_REDUCER_KEY, type InstitutionLoginProps } from './institutionLoginsTypes';
import { isErrorType } from './institutionLoginsUtils';

type State = Pick<RootState, typeof INSTITUTION_LOGINS_REDUCER_KEY>;

export const getInstitutionLoginsStore = (state: State) => state[INSTITUTION_LOGINS_REDUCER_KEY];

export const getInstitutionLoginsById = (state: State) => getInstitutionLoginsStore(state).resourcesById;
export const getLoadPending = (state: State) => getInstitutionLoginsStore(state).loadPending;
export const getLastSyncDate = (state: State) => getInstitutionLoginsStore(state).lastSyncDate;

export const getInstitutionLoginForId = (state: State, id: string) => getInstitutionLoginsById(state).get(id);

export const getIsRefreshingAll = (state: State) => getInstitutionLoginsStore(state).isRefreshingAll;
export const getIdsRefreshing = (state: State) => getInstitutionLoginsStore(state).idsRefreshing;

export const getIsRefreshingSome = (state: State) => getIdsRefreshing(state).size > 0;
export const getIsRefreshing = (state: State) => getIsRefreshingAll(state) || getIdsRefreshing(state).size > 0;

export const getIdsRefreshingWithUserActionsPending = (state: State) =>
  getInstitutionLoginsStore(state).idsRefreshingWithUserActionsPending;

export const getIdsRefreshingWithCancelledUserActions = (state: State) =>
  getInstitutionLoginsStore(state).idsRefreshingWithCancelledUserActions;

export const getCredentialBlobsForRefreshingLogins = (state: State) =>
  getInstitutionLoginsStore(state).credentialBlobsForRefreshingLogins;

export const getCredentialsForRefreshingLogins = (state: State) =>
  getInstitutionLoginsStore(state).credentialsForRefreshingLogins;

export const getDismissedFiIssuesByLoginId = (state: State) =>
  getInstitutionLoginsStore(state).dismissedFiIssuesByLoginId;

export const hasUserActionBeenQueued = (state: State, institutionLoginId: string) =>
  getIdsRefreshingWithUserActionsPending(state).has(institutionLoginId) ||
  getIdsRefreshingWithCancelledUserActions(state).has(institutionLoginId);

export const getActiveConnectedInstitutionLogins = createAppSelector(
  getInstitutionLoginsById,
  accountsSelectors.getAccountsById,
  (institutionLoginsById, accountsById) =>
    institutionLoginsById.filter(
      (login) => login?.aggregators && accountsById.find((account) => account?.institutionLoginId === login.id),
    ),
);

//  for useLoadState compatibility
export const getResourcesById = (state: State) => getInstitutionLoginsStore(state).resourcesById;
export const getIsLoading = (state: State) => getInstitutionLoginsStore(state).isLoading;
export const getError = (state: State) => getInstitutionLoginsStore(state).error;

export const isUserActionPending = createAppSelector(
  getIdsRefreshingWithUserActionsPending,
  (_, props) => (props ? props.institutionLoginIdId : null),
  (idsRefreshingWithUserActionsPending, institutionLoginId) =>
    institutionLoginId == null
      ? idsRefreshingWithUserActionsPending.size > 0
      : idsRefreshingWithUserActionsPending.has(institutionLoginId),
);
export const wasUserActionCancelled = (state: State, institutionLoginId: string) =>
  getIdsRefreshingWithCancelledUserActions(state).has(institutionLoginId);

export const getInstitutionLoginsByName = createAppSelector(getInstitutionLoginsById, (institutionLoginsById) =>
  !institutionLoginsById
    ? null
    : institutionLoginsById.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
      ),
);

export const isInstitutionLoginAmbiguous = createAppSelector(
  getInstitutionLoginsById,
  (_, props) => props.institutionLoginId,
  (institutionLoginsById: ImMap<string, InstitutionLoginProps>, institutionLoginId) => {
    if (!institutionLoginId) {
      return undefined;
    }
    const institutionLogin = institutionLoginsById.get(institutionLoginId);
    if (institutionLogin) {
      const ambiguousInstitutionLogin = institutionLoginsById.find(
        (iInstitutionLogin: InstitutionLoginProps) =>
          iInstitutionLogin?.institutionId === institutionLogin.institutionId &&
          iInstitutionLogin?.id !== institutionLogin.id,
      );
      return ambiguousInstitutionLogin !== undefined;
    }
    return undefined;
  },
);

export const getErrorCount = createAppSelector(
  (state, accountsByInstitutionLoginId) => accountsByInstitutionLoginId,
  getInstitutionLoginsByName,
  (accountsByInstitutionLoginId, institutionLoginsByName) => {
    let count = 0;
    let loginsErrorCount = 0;
    let accountsErrorCount = 0;
    let institutionLogin = {};
    institutionLoginsByName &&
      institutionLoginsByName.forEach((login: RecordOf<InstitutionLoginProps>) => {
        const accounts = accountsByInstitutionLoginId?.get(login.id);
        if (!accounts) {
          // if we don't have any accounts for login, then ingore login
          return;
        }
        const loginAggStatus = login.aggregators?.get(0)?.aggStatus;
        if (isErrorType(loginAggStatus)) {
          loginsErrorCount += 1;
          institutionLogin = login;
        } else {
          accounts?.forEach((account: Account) => {
            const accountAggStatus = account.aggregators?.get(0)?.aggStatus;
            if (accountAggStatus && isErrorType(accountAggStatus as AggStatus)) {
              accountsErrorCount += 1;
              institutionLogin = login;
            }
          });
        }
        count = loginsErrorCount + accountsErrorCount;
      });
    return { count, institutionLogin };
  },
);

export const getTrackingAggregator = createAppSelector(getInstitutionLoginForId, (institutionLogin) => {
  if (institutionLogin?.channel) {
    if (institutionLogin.channel.startsWith('FDS')) {
      return 'INTUIT_FDS';
    }
    if (institutionLogin.channel.startsWith('FDP')) {
      return 'INTUIT_FDP';
    }
    if (institutionLogin.channel.startsWith('FINICITY')) {
      return 'FINICITY';
    }
    if (institutionLogin.channel.startsWith('PLAID')) {
      return 'PLAID';
    }
    if (institutionLogin.channel.startsWith('STRIPE')) {
      return 'STRIPE';
    }
  }
  return institutionLogin?.channel ?? 'UNKNOWN';
});

// This selector returns all "pure" manual accounts. A pure manual is an account that either does not belong to an
// institution-login or belongs to an "unconnected" institution-login. While QCS supports accounts being grouped into
// unconnected-logins, it is only used by Windows to group all manual accounts into a single unconnectted
// intitution-login (this isn't required but is a holdover from Windows syncing to Mint).
//
export const getPureManualAccounts = createAppSelector(
  getInstitutionLoginsById,
  accountsSelectors.getAccountsByInstitutionLoginId,
  (institutionLoginsById, accountsByInstitutionId) => {
    const pureManualAccounts = accountsByInstitutionId.reduce<Account[]>((list, accounts, institutionLoginId) => {
      const institutionLogin = institutionLoginsById.get(institutionLoginId);

      // Special Case: Stripe login has no institutionId but is not a manual account.
      const isStripeAccount = institutionLogin?.channel === 'STRIPE' || institutionLogin?.channel === 'STRIPE_TEST';

      if (
        (Number(institutionLoginId) === 0 ||
          (institutionLogin &&
            (Number(institutionLogin.institutionId) === 0 || !institutionLogin.aggregators?.size))) &&
        !isStripeAccount
      ) {
        accounts.forEach((account: Account) => list.push(account));
      }
      return list;
    }, []);
    return List(pureManualAccounts);
  },
);

export const getFinancialInstitutionsPendo = createAppSelector(
  accountsSelectors.getAccountsById,
  getInstitutionLoginsById,
  (accounts, logins) =>
    accounts.reduce<string[]>((results, account) => {
      const login = account.institutionLoginId !== undefined ? logins.get(account.institutionLoginId) : undefined;
      account.aggregators?.forEach?.((aggregator) => {
        const financialInstitutionPendo = `${aggregator.channel || ''}:${login?.id || ''}:${
          aggregator.aggStatus || ''
        }:${aggregator.aggStatusCode || ''}:${login?.name || ''}`;
        if (!results.includes(financialInstitutionPendo)) {
          results.push(financialInstitutionPendo);
        }
      });
      return results;
    }, []),
);

export const getFiIssueForLogin = createAppSelector(
  (state: State, id: string) => getInstitutionLoginForId(state, id),
  getDismissedFiIssuesByLoginId,
  // The dismissed prop is an override to include dismissed fiIssues in the selector
  (_state: State, _id: string, showDismissed?: boolean) => showDismissed,
  (institutionLogin, dismissedFiIssuesByLoginId, showDismissed) => {
    const fiIssue = institutionLogin?.fiIssue;
    if (fiIssue && (dismissedFiIssuesByLoginId.get(institutionLogin.id) !== fiIssue.issueType || showDismissed)) {
      return fiIssue;
    }
    return undefined;
  },
);

export const isFiIssueDismissedForLogin = createAppSelector(
  (state: State, id: string) => getInstitutionLoginForId(state, id),
  getDismissedFiIssuesByLoginId,
  (institutionLogin, dismissedFiIssuesByLoginId) => {
    return (
      institutionLogin?.fiIssue &&
      dismissedFiIssuesByLoginId.get(institutionLogin.id) === institutionLogin.fiIssue.issueType
    );
  },
);

export const getInstitutionLoginsByChannel = createAppSelector(
  [getInstitutionLoginsById, (_state: State, channel) => channel],
  (institutionLoginsById, channel) => institutionLoginsById.filter((login) => login.channel === channel),
);

export const getInstitutionForInstitutionLogin = createAppSelector(
  getInstitutionLoginsById,
  (_state: State, id: string | undefined) => id,
  getInstitutionsById,
  (institutionLoginsById, id, institutionsById) => {
    if (!id) return undefined;
    const institutionLogin = institutionLoginsById.get(id);
    if (!institutionLogin) return undefined;
    if (institutionLogin.institutionId) {
      return institutionsById.get(institutionLogin.institutionId);
    }
    if (institutionLogin.isConnected && institutionLogin.channel && institutionLogin.institutionId === null) {
      const aggregator = getAggregatorFromChannel(institutionLogin.channel);
      return getStaticInstitutionFromAggregator(aggregator);
    }
    return undefined;
  },
);

export const isRefreshFrequencySupported = createAppSelector(
  (state: State, id?: string) => getInstitutionLoginForId(state, id || ''),
  (institutionLogin) => ['FDP_WSI_OAUTH', 'FDP_DEFAULT', 'FINICITY', 'PLAID'].includes(institutionLogin?.channel || ''),
);
